import { useEffect, useMemo, useState } from 'react';
import { Badge, Menu, MenuProps } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BsClipboardData } from 'react-icons/bs';
import { FiUsers } from 'react-icons/fi';
import { GrDocumentTime } from 'react-icons/gr';
import { LuUsers } from 'react-icons/lu';
import {
  MdOutlineAddTask,
  MdOutlineDashboard,
  MdOutlineFolderCopy,
} from 'react-icons/md';
import { FiAtSign as AtSign } from 'react-icons/fi';
import { TbChalkboard, TbPhone } from 'react-icons/tb';
import Box from 'src/components/Box';
import { UserResponse } from 'src/types';
import { RootState } from 'src/store';
import { useAppTour } from 'src/context/appTourContext';
import { getCurrentCustomer, getCurrentProject } from 'src/utils/localStorage';
import CompleteOnboardingModal from '../CompleteOnboardingModal';
import useProjectAccess from 'src/hooks/useProjectAccess';

type MenuItem = Required<MenuProps>['items'][number];

const linkClassName = 'flex items-center gap-2 font-semibold';
const iconClassName = 'text-2xl';

const MenuLeft = () => {
  const [customer, setCustomer] = useState<any | undefined>(undefined);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const user: UserResponse = useSelector(
    (globalState: any) => globalState.auth.user
  );
  const inboxUnread = useSelector(
    (globalState: RootState) => globalState.inbox.count
  );

  const currentProjectUuid = getCurrentProject();
  
  const project = user.projects.find(
    (project: any) => project.uuid === currentProjectUuid
  );

  const { canSeeCredits, canSeeReports, canSeePlan } = useProjectAccess();

  const { data: projectRetainer } = useSelector(
    (globalState: RootState) => globalState.projectRetainer
  );

  const hasCompleteOnboarding =
    projectRetainer?.customer?.onboarding_step === 1;

  const { setStepTarget } = useAppTour();

  const { pathname } = useLocation();

  const handleBookingClick = (e?: any) => {
    if (!hasCompleteOnboarding) {
      e?.preventDefault?.();
      setShowBookingModal(true);
      return;
    }
  };

  const items: (MenuItem | Record<string, any>)[] = [
    {
      key: 'dashboard',
      href: '/dashboard',
      label: (
        <Link to="/dashboard" className={linkClassName}>
          <MdOutlineDashboard className={iconClassName} />
          Dashboard
        </Link>
      ),
    },
    {
      key: 'inbox',
      href: '/inbox',
      label: (
        <Link to="/inbox" className={linkClassName}>
          <AtSign className={iconClassName} />
          <span className="flex-auto">Mentions</span>
          <Badge count={inboxUnread ?? 0} />
        </Link>
      ),
    },
    {
      key: 'create-request',
      href: '/requests/create',
      label: (
        <Link
          to={'/requests/create'}
          className={linkClassName}
          preventScrollReset={true}
        >
          <MdOutlineAddTask className={iconClassName} />
          Create request
        </Link>
      ),
    },
    {
      key: 'booking',
      href: '/booking',
      label: (
        <Link
          onClick={hasCompleteOnboarding ? undefined : handleBookingClick}
          ref={(node) => {
            if (node) {
              setTimeout(() => {
                setStepTarget(4, node);
              }, 400);
            }
          }}
          to="/booking"
          className={linkClassName}
        >
          <TbPhone className={iconClassName} />
          Book a call
        </Link>
      ),
    },
    ...(project && canSeeCredits
      ? [
          {
            key: 'usage',
            href: '/usage',
            label: (
              <Link to="/usage" className={linkClassName}>
                <GrDocumentTime className={iconClassName} />
                Usage
              </Link>
            ),
          },
        ]
      : []),
    {
      key: 'contacts',
      href: '/contacts',
      label: (
        <Link to="/contacts" className={linkClassName}>
          <FiUsers className={iconClassName} />
          Team members
        </Link>
      ),
    },
    ...(projectRetainer?.customer && (projectRetainer.customer.dashboard_link_1 || projectRetainer.customer.dashboard_link_2) && canSeeReports
      ? [
          {
            key: 'reports',
            href: '/report',
            label: (
              <Link to={projectRetainer.customer.dashboard_link_1 || projectRetainer.customer.dashboard_link_2} className={linkClassName} target="_blank">
                <BsClipboardData className={iconClassName} />
                Reports
              </Link>
            ),
          },
        ]
      : []),
    {
      key: 'assets',
      href: '/project-assets',
      label: (
        <Link to="/project-assets" className={linkClassName}>
          <MdOutlineFolderCopy className={iconClassName} />
          Assets
        </Link>
      ),
    },
    {
      key: 'tutorials',
      href: '/how-to',
      label: (
        <Link to="/how-to" className={linkClassName}>
          <TbChalkboard className={iconClassName} />
          How To's
        </Link>
      ),
    },
  ];

  const selectedItem = useMemo(
    () => items.find((item: any) => {
      if (!item.href) return false;
      const normalizedHref = item.href.replace(/\/$/, '');
      const normalizedPathname = pathname.replace(/\/$/, '');
      return normalizedHref === normalizedPathname;
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname]
  );

  useEffect(() => {
    const customer = getCurrentCustomer();
    if (!customer) return;

    setCustomer(customer);
  }, [user]);

  return (
    <>
      <CompleteOnboardingModal
        visible={showBookingModal}
        onClose={() => setShowBookingModal(false)}
      />

      <Box className="rounded-t-none" noPadding>
        <div className="break-words px-7 py-3">
          <div className="w-full truncate whitespace-nowrap font-bold">
            {customer?.business_name}
          </div>

          {project ? (
            <div className="text-xs">
              {canSeePlan && <span className="capitalize">{project.plan} - </span>}
              <LuUsers className="inline align-text-bottom text-base" />{' '}
              {project?.customer?.contacts_count} members
            </div>
          ) : undefined}
        </div>

        <Menu
          mode="inline"
          items={items as MenuItem[]}
          selectedKeys={selectedItem ? [selectedItem.key] : []}
          style={{ background: 'none', borderInlineEnd: 'none' }}
        />
      </Box>
    </>
  );
};

export default MenuLeft;
